import { first } from 'rxjs/operators';
import { AfterContentInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCheckboxChange } from '@angular/material/checkbox';
import * as _ from 'lodash';
import { TenantService } from '../services/tenant.service';

@Component({
    selector: 'tenant-editor',
    styles: [`
        .right-field {
            padding-left:20px;
        }

        mat-card {
            padding: 35px;
        }

        mat-card-content {
            min-height: 450px;
        }

        mat-card-content.create {
            min-height: 575px;
        }

        mat-checkbox {
            padding-top: 15px;
        }
    `],
    template: `
        <form name="editTenatForm" #editTenantForm="ngForm">
            <mat-card style="box-shadow: none;">
                <mat-card-header>
                    <mat-card-title>
                        <h2 *ngIf="currentTenant">{{ currentTenant.id ? 'Edit' : 'Create' }} Site</h2>
                    </mat-card-title>
                </mat-card-header>
                <mat-card-content fxLayout="column" [ngClass]="{'create': !currentTenant.id}">
                    <div fxFlex fxLayout="row">
                        <mat-form-field fxFlex="300px">
                            <input name="tenantName" #tenantName="ngModel" matInput placeholder="Tenant Name" [(ngModel)]="currentTenant.name" required>
                            <mat-error *ngIf="tenantName.hasError('required')">required</mat-error>
                            </mat-form-field>
                        <mat-form-field class="right-field" fxFlex="300px">
                            <input validateUniqueDomain [originalDomain]="originalTenant.hostName" name="tenantHostname" #tenantHostname="ngModel" matInput placeholder="Hostname" [(ngModel)]="currentTenant.hostName" required>
                            <span matSuffix>.attensa.net</span>
                            <mat-error *ngIf="tenantHostname.hasError('required')">required</mat-error>
                            <mat-error *ngIf="tenantHostname.hasError('uniqueDomain')">domain already taken</mat-error>
                        </mat-form-field>
                        <div fxFlex></div>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top: 30px; padding-bottom: 30px;">
                        <mat-form-field fxFlex="300px">
                            <input name="briefingFromAddress" #briefingFromAddress="ngModel" matInput placeholder="Briefing From Address" [(ngModel)]="currentTenant.settings.briefingsFromAddress" required maxlength="256" pattern="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$">
                            <mat-error *ngIf="briefingFromAddress.hasError('required')">required</mat-error>
                            <mat-error *ngIf="briefingFromAddress.hasError('pattern')">must be a valid email address</mat-error>
                        </mat-form-field>
                        <mat-form-field class="right-field" fxFlex="300px">
                            <input name="briefingReplyAddress" #briefingReplyAddress="ngModel" matInput placeholder="Briefing Reply To Address" [(ngModel)]="currentTenant.settings.briefingsReplyToAddress" required maxlength="256" pattern="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$">
                            <mat-error *ngIf="briefingReplyAddress.hasError('required')">required</mat-error>
                            <mat-error *ngIf="briefingReplyAddress.hasError('pattern')">must be a valid email address</mat-error>
                        </mat-form-field>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-bottom: 30px;">
                        <mat-form-field fxFlex="300px">
                            <input name="sendgridUsername" #sendgridUsername="ngModel" matInput placeholder="Sendgrid Username" [(ngModel)]="currentTenant.settings.emailUsername" maxlength="256">
                        </mat-form-field>
                        <mat-form-field class="right-field" fxFlex="300px">
                            <input name="sendgridPassword" #sendgridPassword="ngModel" matInput placeholder="Sendgrid Password" [(ngModel)]="currentTenant.settings.emailPassword" maxlength="256" [type]="showSendgridPassword ? 'text' : 'password'">
                            <button mat-button 
                                    matSuffix 
                                    mat-icon-button 
                                    aria-label="Toggle password visibility" 
                                    (click)="showSendgridPassword = !showSendgridPassword" 
                                    [matTooltip]="showSendgridPassword ? 'Hide password' : 'Show password'">
                                <mat-icon>{{ showSendgridPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
                            </button>
                        </mat-form-field>
                    </div>
                    <div fxFlex fxLayout="row">
                        <mat-form-field fxFlex="300px">
                            <input name="logoUrl" matInput placeholder="Logo Url" [(ngModel)]="currentTenant.settings.logoUrl">
                        </mat-form-field>
                    </div>
                    <div *ngIf="currentTenant.id" fxFlex fxLayout="row" style="padding-top:25px;">
                        <mat-checkbox name="isCreateStreamsEnabled" [(ngModel)]="isCreateStreamsEnabled" (change)="isCreateStreamsEnabledChanged()">Site can create sources and topics</mat-checkbox>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-checkbox name="selfSignupConfiguration" [(ngModel)]="settings.selfSignupAllowed" (change)="settingChanged($event, 'selfSignupConfiguration')">User can self enroll</mat-checkbox>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-checkbox name="hideCategoryReader" [(ngModel)]="settings.hideCategories" (change)="settingChanged($event, 'hideCategories')">Hide Categories for reader</mat-checkbox>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-checkbox fxFlexAlign="start center" 
                                      name="limitProductSources"
                                      [disabled]="productCount !== undefined && productCount > 0" 
                                      [matTooltip]="productCount !== undefined && productCount > 0 ? 'Remove all products to switch modes' : 'Publisher Mode'"
                                      [(ngModel)]="settings.limitProductSources" 
                                      (change)="settingChanged($event, 'limitProductSources')">
                            Publisher mode (restricts subscribers to sources)
                        </mat-checkbox>
                        <mat-form-field fxFlexOffset="25px">
                            <input name="productOversubscribedMessage" 
                                   matInput
                                   [disabled]="!settings.limitProductSources" 
                                   placeholder="Oversubscribed Message" 
                                   [(ngModel)]="currentTenant.settings.productOversubscribedMessage">
                        </mat-form-field>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-checkbox name="publicTopics" [(ngModel)]="settings.publicTopics" (change)="settingChanged($event, 'publicTopics')">Enable publish topics</mat-checkbox>
                    </div>
                    <div fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-checkbox name="autoTopicPublish" 
                                      [(ngModel)]="settings.autoTopicPublish" 
                                      (change)="settingChanged($event, 'autoTopicPublish')">Automatically publish topics</mat-checkbox>
                    </div>
                    <div *ngIf="!currentTenant.id" style="padding-top:30px;"><h2>Admin User</h2></div>
                    <div *ngIf="!currentTenant.id" fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-form-field>
                            <input name="firstName" #firstName="ngModel" matInput placeholder="First" [(ngModel)]="adminUser.firstName" required>
                            <mat-error *ngIf="firstName.hasError('required')">required</mat-error>
                        </mat-form-field>
                        <mat-form-field class="right-field">
                            <input name="lastName" #lastName="ngModel" matInput placeholder="Last" [(ngModel)]="adminUser.lastName" required>
                            <mat-error *ngIf="lastName.hasError('required')">required</mat-error>
                        </mat-form-field>
                    </div>
                    <div *ngIf="!currentTenant.id" fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-form-field>
                            <input name="password" 
                                    #password="ngModel" 
                                    matInput 
                                    type="password" 
                                    placeholder="Password" 
                                    [(ngModel)]="adminUser.passwordHash" 
                                    validateEqual="confirmPassword" 
                                    reverse="true" 
                                    pattern="^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$" 
                                    required>
                            <mat-error *ngIf="password.hasError('required')">required</mat-error>
                            <mat-error *ngIf="password.hasError('pattern')">must contain at least one upper case letter, one lowercase letter, one number and be at least 8 characters long.</mat-error>
                        </mat-form-field>
                        <mat-form-field class="right-field">
                            <input name="confirmPassword" #confirmPassword="ngModel" matInput type="password" placeholder="Confirm Password" [(ngModel)]="adminUser.confirmPassword" validateEqual="password" reverse="false" required>
                            <mat-error *ngIf="confirmPassword.hasError('required')">required</mat-error>
                            <mat-error *ngIf="confirmPassword.hasError('validateEqual')">passwords must match</mat-error>
                        </mat-form-field>
                    </div>
                    <div *ngIf="!currentTenant.id" fxFlex fxLayout="row" style="padding-top:15px;">
                        <mat-form-field>
                            <input name="emailAddress" #adminEmailAddress="ngModel" matInput placeholder="Email" [(ngModel)]="adminUser.emailAddress" pattern="[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,63}$" required maxlength="256">
                            <mat-error *ngIf="adminEmailAddress.hasError('required')">required</mat-error>
                            <mat-error *ngIf="adminEmailAddress.hasError('pattern')">must be a valid email address</mat-error>
                        </mat-form-field>
                    </div>
                </mat-card-content>
                <mat-card-actions>
                    <button mat-raised-button type="button" color="primary" [disabled]="editTenantForm.form.invalid || isUnchanged()" (click)="save()">Save</button>
                    <button mat-button type="button" (click)="cancel()">Cancel</button>
                </mat-card-actions>
            </mat-card>
        </form>        
    `
})
export class TenantEditorComponent implements OnInit, AfterContentInit {
    @Input() tenant: any;
    @Input() productCount: number;
    @Output() tenantSaved = new EventEmitter<object>();
    @Output() cancelled = new EventEmitter<null>();
    @ViewChild('tenantName', { static: true }) tenantName;
    @ViewChild('tenantHostname', { static: true }) tenantHostname;
    @ViewChild('briefingFromAddress', { static: true }) briefingFromAddress;
    @ViewChild('briefingReplyAddress', { static: true }) briefingReplyAddress;
    showSendgridPassword: boolean;
    isCreateStreamsEnabled: boolean;
    currentTenant: any;
    originalTenant: any;
    adminUser: { firstName?, middleName?, lastName?, passwordHash?, confirmPassword?, emailAddress? } = {};
    settings;

    constructor(private tenantService: TenantService, private snackBar: MatSnackBar){}

    isCreateStreamsEnabledChanged = () => {
        if (this.isCreateStreamsEnabled) {
            _.find(this.currentTenant.tenantRoles, {type: 'TENANT_ADMIN'}).permittedActions.push('CREATE_STREAM');
        } else {
            _.remove(_.find(this.currentTenant.tenantRoles, {type: 'TENANT_ADMIN'}).permittedActions, item => item === 'CREATE_STREAM');
        }
    }

    settingChanged = (change: MatCheckboxChange, setting: string) => this.currentTenant.settings[setting] = change.checked ? 'true': undefined;

    isUnchanged = () => _.isEqual(this.currentTenant, this.originalTenant);

    private reset = () => {
        this.currentTenant = _.cloneDeep(this.tenant);
        this.currentTenant.hostName = this.currentTenant.hostName ? this.currentTenant.hostName.split('.')[0] : this.currentTenant.hostname;
        this.originalTenant = _.cloneDeep(this.currentTenant);
        this.isCreateStreamsEnabled = this.currentTenant.id && _.find(this.currentTenant.tenantRoles, {type: 'TENANT_ADMIN'}).permittedActions.indexOf('CREATE_STREAM') !== -1;

        this.settings = {
            selfSignupAllowed: this.currentTenant.settings.selfSignupConfiguration === 'true',
            hideCategories: this.currentTenant.settings.hideCategories === 'true', 
            topicInviteEnabled: false, 
            limitProductSources: this.currentTenant.settings.limitProductSources === 'true',
            publicTopics: this.currentTenant.settings.publicTopics === 'true',
            autoTopicPublish: this.currentTenant.settings.autoTopicPublish === 'true'  
        };
    };

    cancel = (noEmit: boolean = false) => {
        this.reset();

        if(!noEmit){
            this.cancelled.emit();            
        }
    }

    tenantUpdateHandler = updatedTenant => {
        this.tenantSaved.emit(updatedTenant.data ? updatedTenant.data : updatedTenant);
        this.currentTenant = updatedTenant;
        this.currentTenant.hostName = this.currentTenant.hostName ? this.currentTenant.hostName.split('.')[0] : this.currentTenant.hostname;
        this.originalTenant = _.cloneDeep(this.currentTenant)
    }

    save = () => {
        this.currentTenant.hostName = this.currentTenant.hostName.toLowerCase() + '.attensa.net';
        if(this.currentTenant.id){
            this.tenantService.updateTenant(this.currentTenant).pipe(first())
            .subscribe(
                updatedTenant => this.tenantUpdateHandler(updatedTenant), 
                err => this.snackBar.open('Oops, we were unable to update tenant, please contact your site administrator.', 'OK', { duration: 10000, panelClass: ['errorSnackbar']})
            );
        } else {
            const submittedTenant = _.cloneDeep(this.currentTenant);
            _.forEach(['reportingExports', 'defaultPages'], attr => submittedTenant.settings[attr] = JSON.stringify(submittedTenant.settings[attr]));             
            this.tenantService.createTenant({ 
                adminUser: this.adminUser, 
                tenant: submittedTenant, 
                configuration: _(submittedTenant.settings).map((value, key) => ({ key, value })).filter(obj => !!obj.value).value()
            }).pipe(first())
            .subscribe(
                updatedTenant => this.tenantUpdateHandler(updatedTenant), 
                err => this.snackBar.open('Oops, we were unable to create tenant, please contact your site administrator.', 'OK', { duration: 10000, panelClass: ['errorSnackbar']})
            );
        }
    }

    ngOnInit() {
        this.reset();
    }

    ngAfterContentInit() {
        if(this.currentTenant.id) {
            this.tenantName.control.markAsTouched();
            this.tenantHostname.control.markAsTouched();
            this.briefingFromAddress.control.markAsTouched();
            this.briefingReplyAddress.control.markAsTouched();
        }
    };
}
